import { FilterPanelBody } from './filter-panel-body'
import { useFilterContext } from '../../context/filter-context'
import { ReactElement } from 'react'
import { Container } from 'react-bootstrap'
import { Button } from 'src/component/ui/button'
import { Drawer, DrawerClose, DrawerContent, DrawerTitle, DrawerTrigger } from 'src/component/ui/drawer'
import { FilterIcon } from 'src/component/util/images'

/**
 * Mobile header for the Workouts page (shown below `lg`, where the desktop filter rail is hidden):
 * a "Workouts" title and a copper-outlined menu button that opens the filter panel as a bottom
 * sheet. The sheet reuses FilterPanelBody, so it's identical to the desktop rail; selections apply
 * in real time, so "Show results" just closes the sheet to reveal the updated grid. A badge on the
 * button surfaces the active filter count (categories + the Completed Workouts toggle).
 */
export const MobileFilters = (): ReactElement => {
  const { activeFilterCount, hideCompleted } = useFilterContext()
  const badgeCount = activeFilterCount + (hideCompleted ? 1 : 0)

  return (
    <Container className="d-lg-none py-4">
      <div className="flex items-center justify-between">
        <h1 className="font-evogria text-primary text-4xl uppercase">Workouts</h1>
        <Drawer>
          <DrawerTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              aria-label="Open filters"
              className="relative h-6 w-6 rounded-md border text-primary"
            >
              <FilterIcon className="h-3.5 w-3.5" />
              {badgeCount > 0 && (
                <span
                  data-testid="filter-count-badge"
                  className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1 font-roboto text-xs text-white"
                >
                  {badgeCount}
                </span>
              )}
            </Button>
          </DrawerTrigger>
          <DrawerContent className="max-h-[85vh] bg-black">
            <DrawerTitle className="sr-only">Filters</DrawerTitle>
            {/* Body scrolls within the sheet; the footer stays pinned at the bottom. */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              <FilterPanelBody />
            </div>
            <div className="border-top border-secondary-gray p-4">
              <DrawerClose asChild>
                <Button className="w-full">Show results</Button>
              </DrawerClose>
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </Container>
  )
}
